/**
 * 画布视频生成器
 *
 * 走画布应用专属端点 videoApi.generateFromCanvas（POST /canvas/videos/generations，
 * source='canvas'） + 自管轮询（videoApi 没暴露 poll 工具，这里写最小轮询，每 5s 查一次）。
 *
 * 流程与 imageGenerator 类似：
 *  1. 单条：在 generate_video 节点右侧创建占位 video 节点，提交一次（n=1）后轮询
 *  2. 多条（count > 1）：结果收进单个视频组节点，循环单条并发提交（每条 n=1），
 *     逐条回填槽位，整批终态后把成功的段按提交顺序转正进组
 *  3. 拿到 jobId，轮询 videoApi.getJob 直到 completed/failed
 */
import { videoApi } from '../../../baselines/video/services/videoApi';
import type { VideoMode, VideoJobDetail, CameraMovement, MotionIntensity } from '../../../baselines/video/services/videoApi';
import toast from '../../../components/Toast';
import { boardActions, getBoardState } from '../state/boardStore';
import { collectUpstreamImageAssetGroups } from '../state/upstream';
import { resolveAtReferences } from '../atReference';
import { CANVAS_NODE_REMOVED, makeNode, nid } from '../utils';
import { VIDEO_GROUP_MAX_CLIPS } from '../constants';
import { normalizeJobStatus } from '../utils/jobStatus';
import { toPersistableMediaUrl } from '../utils/persistableMediaUrl';
import { resolveBlobUrlsForApi } from '../imageStorage';
import { reuseAutoChildSlot } from './resultSlots';
import {
  buildGeneratingSlots,
  patchVideoGroupSlot,
  settleVideoGroupSlots,
} from './videoGroupOps';
import { getCachedVideoModels } from '../modelCache';
import { getModelCapabilitiesForMode } from '../../../baselines/video/hooks/useVideoModelCapabilities';
import type {
  CanvasNode,
  GenerateVideoNodeData,
  VideoGroupNodeData,
  VideoNodeData,
} from '../types';

/** 每个生视频节点的进行中任务追踪：abort 旧轮询 + 版本号防止旧任务 finally 覆盖新任务状态 */
const videoAbortMap = new Map<string, AbortController>();
const videoVersionMap = new Map<string, number>();

/** 把 modelId 映射为视频模型展示名；缓存未加载或未命中时回退 modelId 本身 */
async function resolveVideoModelLabel(modelId: string): Promise<string> {
  try {
    const models = await getCachedVideoModels();
    return models.find((m) => m.modelId === modelId)?.name ?? modelId;
  } catch {
    return modelId;
  }
}

/**
 * 当前模型 + 模式是否支持生成配音（音频）。
 *
 * 取不到模型信息时按「不支持」处理：宁可漏开声音，也不能按音频倍率误扣积分。
 */
async function resolveAudioCapability(modelId: string, mode: VideoMode): Promise<boolean> {
  try {
    const models = await getCachedVideoModels();
    return getModelCapabilitiesForMode(
      models.find((m) => m.modelId === modelId),
      mode
    ).enableAudio;
  } catch {
    return false;
  }
}

interface RunVideoGenerationOptions {
  /** 触发的 generate_video 节点 */
  generateNode: CanvasNode<GenerateVideoNodeData>;
  /** 聚合后的 prompt */
  prompt: string;
  /** 上游图片节点解析得到的参考图（仅 img2video 模式使用） */
  referenceImages: string[];
  /** first_last_frame 模式的首帧 / 尾帧 */
  firstFrameImage?: string;
  lastFrameImage?: string;
  /**
   * 上游视频节点的参考视频（画布视频消费的唯一例外）。
   * 仅当节点当前模型+模式支持参考视频时由调用方传入；服务端会解析
   * 网关 URL / storage key 并做能力与数量校验。
   */
  referenceVideoUrls?: string[];
  /** 与 referenceVideoUrls 下标对齐的时长（秒）；输入侧计费口径，缺省按 0 */
  referenceVideoDurations?: number[];
  /**
   * 上游声音节点的参考音频（storage key 列表）。
   * 仅当节点当前模型+模式支持参考音频时由调用方传入；服务端会解析
   * storage key 为上游可访问的 ticket URL 并做能力与数量校验。
   */
  audioReferenceUrls?: string[];
}

const POLL_INTERVAL_MS = 5000;
const MAX_POLL_ATTEMPTS = 200; // 200 * 5s ≈ 16 分钟

/** 网络类异常连续达到阈值才判死，单次抖动不终止轮询 */
const MAX_CONSECUTIVE_FETCH_ERRORS = 3;

/** 轮询视频任务直到终态（jobRecovery 刷新后续接恢复时复用） */
export async function pollVideoJob(
  jobId: string,
  onUpdate: (job: VideoJobDetail) => void,
  signal?: AbortSignal
): Promise<VideoJobDetail> {
  let consecutiveFetchErrors = 0;
  for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');

    let job: VideoJobDetail;
    try {
      job = await videoApi.getJob(jobId);
    } catch (error) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      consecutiveFetchErrors++;
      if (consecutiveFetchErrors >= MAX_CONSECUTIVE_FETCH_ERRORS) throw error;
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
      continue;
    }
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    consecutiveFetchErrors = 0;
    // onUpdate 的错误（节点删除哨兵等业务性异常）不做容错，立即终止
    onUpdate(job);
    if (job.status === 'completed') return job;
    if (job.status === 'failed' || job.status === 'timeout') {
      throw new Error(job.errorMessage || (job.status === 'timeout' ? '视频生成超时' : '视频生成失败'));
    }
    await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
  }
  throw new Error('视频生成超时，请稍后查看任务');
}

export async function runVideoGeneration(opts: RunVideoGenerationOptions): Promise<void> {
  const { generateNode, prompt, referenceImages, firstFrameImage, lastFrameImage, referenceVideoUrls, referenceVideoDurations, audioReferenceUrls } = opts;

  // 重新生成时取消旧轮询：abort 旧 AbortController，旧轮询静默终止
  videoAbortMap.get(generateNode.id)?.abort();
  const abortController = new AbortController();
  videoAbortMap.set(generateNode.id, abortController);
  const myVersion = (videoVersionMap.get(generateNode.id) ?? 0) + 1;
  videoVersionMap.set(generateNode.id, myVersion);
  const isCurrentVersion = () => videoVersionMap.get(generateNode.id) === myVersion;

  const data = generateNode.data;
  // 提前取出并收窄：后续多处 await 会让 TS 丢失 data.modelId 的非空收窄
  const modelId = data.modelId;

  if (!modelId) throw new Error('请先选择视频模型');
  if (!prompt.trim()) throw new Error('请填写提示词');

  const mode: VideoMode = (data.mode || 'text2video') as VideoMode;
  const duration = Math.max(1, Math.min(60, data.duration ?? 5));
  const resolution = data.resolution || '720p';
  const aspectRatio = data.aspectRatio || '16:9';

  // @图N 引用解析（与画布生图同口径，统一放在这里：节点按钮 / 运行链路 / 执行全部共用）：
  // @图N 按**稳定图号**解析（与连线位置无关，增删其他节点不漂移）；提示词含标记时
  // 参考图切换为标记显式引用的上游资产（按标记出现顺序去重），标记替换为「参考图」
  // 后随提示词提交；无标记时维持连线全量聚合的现行为。
  const { nodes: graphNodes, edges: graphEdges } = getBoardState();
  const knownRefNos = new Set(
    graphNodes
      .filter((n) => n.type === 'image' || n.type === 'image_group')
      .map((n) => (n.data as Record<string, unknown>).refNo)
      .filter((v): v is number => typeof v === 'number')
  );
  const atResult = resolveAtReferences(
    prompt,
    collectUpstreamImageAssetGroups(generateNode.id, graphNodes, graphEdges),
    knownRefNos
  );
  if (atResult && atResult.warnings.length > 0) {
    toast(atResult.warnings.join('；'));
  }
  const finalPrompt = atResult ? atResult.prompt : prompt;
  const finalReferenceImages = atResult ? atResult.referenceImages : referenceImages;

  // 首尾帧模式要求恰好 2 张参考图（第一张作首帧、第二张作尾帧）：
  // 后端只校验首尾帧都存在（≥2 取前两张），多余连线会静默丢弃并误导用户，
  // 与节点置灰同口径在这里提前拦截（按 @引用解析后的实际张数判断）。img2video
  // 的数量上限由后端 validateVideoRequest 拒绝并返回可读错误，不重复校验。
  if (mode === 'first_last_frame' && finalReferenceImages.length !== 2) {
    throw new Error(
      `首尾帧模式需要恰好 2 张参考图：第一张作首帧、第二张作尾帧（当前 ${finalReferenceImages.length} 张），请调整连线`
    );
  }

  // 标记 generate 节点进入生成中状态。置位必须先于下方所有 await：
  // 守卫读到的 generating 与置位之间隔着任何 await 都会被并发入口
  // （另一条链 / 执行全部按钮）穿透，重复提交、重复扣积分
  boardActions.updateNodeData(generateNode.id, { generating: true });

  let resolvedReferenceImages: string[];
  let finalFirstFrame: string | undefined;
  let finalLastFrame: string | undefined;
  try {
    // 本地上传的图片是 blob: URL，后端无法访问，提交前统一解析为 data URL（失败条目被过滤）
    resolvedReferenceImages = await resolveBlobUrlsForApi(finalReferenceImages);
    // 首尾帧逐个解析，解析失败置空比错传 blob: 更安全。
    // @引用生效时首尾帧取自解析后的引用列表（与张数校验同口径，避免错位）
    const firstSource = atResult ? finalReferenceImages[0] : firstFrameImage;
    const lastSource = atResult ? finalReferenceImages[1] : lastFrameImage;
    finalFirstFrame = firstSource
      ? (await resolveBlobUrlsForApi([firstSource]))[0]
      : undefined;
    finalLastFrame = lastSource
      ? (await resolveBlobUrlsForApi([lastSource]))[0]
      : undefined;
  } catch (err) {
    // 准备阶段失败：复位生成中标志（提交后的复位由下方 catch/finally 负责）
    if (isCurrentVersion()) {
      boardActions.updateNodeData(generateNode.id, { generating: false });
    }
    throw err;
  }

  const modelLabel = await resolveVideoModelLabel(modelId);
  // 配音开关按当前模型 + 模式能力收敛：节点未显示该开关（模型不支持）时，
  // 存量 data 里可能仍是默认的 true，直接提交会让后端按音频倍率多扣积分
  const audioEnabled =
    (await resolveAudioCapability(modelId, mode)) && Boolean(data.enableAudio);

  // 生成数量：与视频工作台口径一致（1 ~ maxJobsPerSubmit），再按视频组容量兜底
  const count = Math.max(1, Math.min(data.count ?? 1, VIDEO_GROUP_MAX_CLIPS));

  // 批量（count > 1）：结果收进单个视频组节点，画布只占一格，顺序即拼接顺序，
  // 可直接用「导出为一段视频」合成。提交方式与画布生图一致：循环单条并发提交
  // （每条 n=1），中途失败只影响单个槽位，不拖垮整批。
  if (count > 1) {
    let groupNodeId: string | null = null;
    try {
      // 容量预校验必须在预填槽位之前：校验失败时组内不能留下永远 processing 的悬空槽位
      const existingCount = findAutoVideoGroup(generateNode.id)?.data.videoUrls?.length ?? 0;
      if (existingCount + count > VIDEO_GROUP_MAX_CLIPS) {
        throw new Error(
          `视频组最多 ${VIDEO_GROUP_MAX_CLIPS} 段，组内已有 ${existingCount} 段，最多再生成 ${VIDEO_GROUP_MAX_CLIPS - existingCount} 个`
        );
      }

      // 复用已有视频组卡片（保留组内已合并的内容，本轮结果追加到末尾）
      groupNodeId = reuseAutoChildSlot(generateNode, 'video_group', {
        create: () =>
          makeNode<VideoGroupNodeData>(
            'video_group',
            { x: generateNode.x + generateNode.width + 60, y: generateNode.y },
            {
              videoUrls: [],
              coverUrls: [],
              durations: [],
              generatingSlots: buildGeneratingSlots(count),
              _autoGenerated: true,
            } as VideoGroupNodeData & { _autoGenerated: boolean },
            undefined,
            generateNode.zIndex
          ),
        reset: (nodeId) =>
          boardActions.updateNodeData(nodeId, { generatingSlots: buildGeneratingSlots(count) }),
      });
      const targetGroupId = groupNodeId;

      const tasks = Array.from({ length: count }, (_, i) =>
        (async () => {
          try {
            const submission = await videoApi.generateFromCanvas({
              modelId,
              mode,
              prompt: finalPrompt,
              duration,
              resolution,
              aspectRatio,
              cameraMovement: data.cameraMovement as CameraMovement | undefined,
              motionIntensity: data.motionIntensity as MotionIntensity | undefined,
              enableAudio: audioEnabled,
              referenceImages:
                mode === 'img2video' && resolvedReferenceImages.length > 0
                  ? resolvedReferenceImages
                  : undefined,
              firstFrameImage: mode === 'first_last_frame' ? finalFirstFrame : undefined,
              lastFrameImage: mode === 'first_last_frame' ? finalLastFrame : undefined,
              videoReferenceUrls:
                referenceVideoUrls && referenceVideoUrls.length > 0
                  ? referenceVideoUrls
                  : undefined,
              videoReferenceDurations:
                referenceVideoUrls && referenceVideoUrls.length > 0
                  ? referenceVideoUrls.map((_, idx) => referenceVideoDurations?.[idx] ?? 0)
                  : undefined,
              audioReferenceUrls:
                audioReferenceUrls && audioReferenceUrls.length > 0
                  ? audioReferenceUrls
                  : undefined,
              n: 1,
              clientRequestId: nid(),
            });
            const jobId = submission.jobIds[0];
            if (!jobId) throw new Error('提交视频任务失败');
            patchVideoGroupSlot(targetGroupId, i, { jobId });

            await pollVideoJob(jobId, (job) => {
              // 组节点被撤销/删除时中断轮询，避免幽灵写入
              if (!getBoardState().nodes.some((n) => n.id === targetGroupId)) {
                throw new Error(CANVAS_NODE_REMOVED);
              }
              writeVideoGroupSlotFromJob(targetGroupId, i, job);
            }, abortController.signal);
          } catch (err) {
            // abort（重新生成取消旧轮询）静默终止，不标记失败
            if (err instanceof DOMException && err.name === 'AbortError') return;
            patchVideoGroupSlot(targetGroupId, i, {
              status: 'failed',
              errorMessage: err instanceof Error ? err.message : '视频生成失败',
            });
            // 组被删除属于主动取消，不算失败；其余错误上抛，让链式执行中断
            if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
            throw err;
          }
        })()
      );

      const results = await Promise.allSettled(tasks);
      const firstRejected = results.find(
        (r): r is PromiseRejectedResult => r.status === 'rejected'
      );
      if (firstRejected) throw firstRejected.reason;
    } finally {
      // 成功的槽位按提交顺序转正进 videoUrls；失败的留在槽位里供用户查看原因并移除
      if (groupNodeId) settleVideoGroupSlots(groupNodeId);
      // 旧任务被 abort 时不应覆盖新任务的 generating 标记
      if (!isCurrentVersion()) return;
      const latest = groupNodeId
        ? getBoardState().nodes.find((n) => n.id === groupNodeId)
        : undefined;
      const jobIds = ((latest?.data as VideoGroupNodeData | undefined)?.generatingSlots ?? [])
        .map((slot) => slot.jobId)
        .filter((id): id is string => !!id);
      boardActions.updateNodeData(generateNode.id, { generating: false, lastJobIds: jobIds });
    }
    return;
  }

  // 单条：复用已有结果卡片（原地清空，保留位置与手工连线），没有才新建
  const slotNodeId = reuseAutoChildSlot(generateNode, 'video', {
    create: () =>
      makeNode<VideoNodeData>(
        'video',
        { x: generateNode.x + generateNode.width + 60, y: generateNode.y },
        {
          status: 'processing',
          prompt: finalPrompt,
          videoUrl: null,
          coverUrl: null,
          jobId: null,
          modelLabel,
          aspectRatio: data.aspectRatio ?? null,
          _autoGenerated: true,
        } as VideoNodeData & { _autoGenerated: boolean },
        undefined,
        generateNode.zIndex
      ),
    reset: (nodeId) =>
      boardActions.updateNodeData(nodeId, {
        status: 'processing',
        prompt: finalPrompt,
        videoUrl: null,
        coverUrl: null,
        jobId: null,
        errorMessage: null,
        modelLabel,
        aspectRatio: data.aspectRatio ?? null,
      }),
  });

  try {
    const submission = await videoApi.generateFromCanvas({
      modelId,
      mode,
      prompt: finalPrompt,
      duration,
      resolution,
      aspectRatio,
      cameraMovement: data.cameraMovement as CameraMovement | undefined,
      motionIntensity: data.motionIntensity as MotionIntensity | undefined,
      enableAudio: audioEnabled,
      referenceImages: mode === 'img2video' && resolvedReferenceImages.length > 0 ? resolvedReferenceImages : undefined,
      firstFrameImage: mode === 'first_last_frame' ? finalFirstFrame : undefined,
      lastFrameImage: mode === 'first_last_frame' ? finalLastFrame : undefined,
      videoReferenceUrls:
        referenceVideoUrls && referenceVideoUrls.length > 0 ? referenceVideoUrls : undefined,
      videoReferenceDurations:
        referenceVideoUrls && referenceVideoUrls.length > 0
          ? referenceVideoUrls.map((_, i) => referenceVideoDurations?.[i] ?? 0)
          : undefined,
      audioReferenceUrls:
        audioReferenceUrls && audioReferenceUrls.length > 0
          ? audioReferenceUrls
          : undefined,
      n: 1,
      clientRequestId: nid(),
    });

    const jobId = submission.jobIds[0];
    if (!jobId) throw new Error('提交视频任务失败');
    boardActions.updateNodeData(slotNodeId, { jobId });

    // 轮询任务状态（占位节点被撤销/删除时中断轮询，避免幽灵写入）
    await pollVideoJob(jobId, (job) => {
      if (!getBoardState().nodes.some((n) => n.id === slotNodeId)) {
        throw new Error(CANVAS_NODE_REMOVED);
      }
      boardActions.updateNodeData(slotNodeId, {
        status: normalizeJobStatus(job.status),
        videoUrl: toPersistableMediaUrl(job.resultUrl),
        coverUrl: toPersistableMediaUrl(job.coverUrl),
        videoDurationSec: job.resultDuration ?? job.duration ?? null,
      });
    }, abortController.signal);
  } catch (err) {
    // abort（重新生成取消旧轮询）静默终止，不标记失败
    if (err instanceof DOMException && err.name === 'AbortError') return;
    boardActions.updateNodeData(slotNodeId, {
      status: 'failed',
      errorMessage: err instanceof Error ? err.message : '视频生成失败',
    });
    // 占位节点被删除属于主动取消；其余错误上抛，让链式执行中断
    if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
    throw err;
  } finally {
    // 旧任务被 abort 时不应覆盖新任务的 generating 标记
    if (!isCurrentVersion()) return;
    const latest = getBoardState().nodes.find((n) => n.id === slotNodeId);
    boardActions.updateNodeData(generateNode.id, {
      generating: false,
      lastJobIds: latest && (latest.data as VideoNodeData).jobId
        ? [(latest.data as VideoNodeData).jobId as string]
        : [],
    });
  }
}

// ============================================================================
// 批量生成（视频组槽位）辅助
// ============================================================================

/**
 * 查找 generate 节点下已存在的「自动生成视频组」子节点。
 *
 * 批量生成的容量预校验必须在预填槽位之前完成，而 reuseAutoChildSlot 会在
 * 复用/新建时立刻写入槽位，所以这里先单独探测一次。
 */
function findAutoVideoGroup(generateNodeId: string): CanvasNode<VideoGroupNodeData> | null {
  const state = getBoardState();
  const childIds = new Set(
    state.edges
      .filter((e) => e.sourceNodeId === generateNodeId && e.sourceHandle === 'right')
      .map((e) => e.targetNodeId)
  );
  const found = state.nodes.find(
    (n) =>
      childIds.has(n.id) &&
      n.type === 'video_group' &&
      (n.data as Record<string, unknown>)._autoGenerated === true
  );
  return (found as CanvasNode<VideoGroupNodeData> | undefined) ?? null;
}

/** 轮询回调：把任务终态与结果写入组内对应槽位；中间态不写，避免 5s 一次空写 */
function writeVideoGroupSlotFromJob(
  groupNodeId: string,
  index: number,
  job: VideoJobDetail
): void {
  const status = normalizeJobStatus(job.status);
  if (status !== 'completed' && status !== 'failed') return;
  const completed = status === 'completed';
  patchVideoGroupSlot(groupNodeId, index, {
    status,
    url: completed ? toPersistableMediaUrl(job.resultUrl) : null,
    coverUrl: completed ? toPersistableMediaUrl(job.coverUrl) : null,
    durationSec: completed ? job.resultDuration ?? job.duration ?? null : null,
    errorMessage: completed ? null : '视频生成失败',
  });
}

/**
 * 刷新后恢复：续接视频组内未终态槽位的轮询，整批收敛后把成功的段转正。
 *
 * 无 jobId 且未终态的槽位（提交瞬间刷新）无从续接轮询，按中断失败收敛——
 * 否则这些槽位会永远停在 processing，用户既拿不到结果也没有重试入口。
 */
export async function resumeVideoGroupSlots(groupNodeId: string): Promise<void> {
  const node = getBoardState().nodes.find((n) => n.id === groupNodeId);
  const slots = (node?.data as VideoGroupNodeData | undefined)?.generatingSlots ?? [];
  if (slots.length === 0) return;

  await Promise.allSettled(
    slots.map((slot, i) =>
      (async () => {
        if (slot.status === 'completed' || slot.status === 'failed') return;
        if (!slot.jobId) {
          if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) return;
          patchVideoGroupSlot(groupNodeId, i, {
            status: 'failed',
            errorMessage: '页面刷新导致生成中断，请重试',
          });
          return;
        }
        try {
          await pollVideoJob(slot.jobId, (job) => {
            if (!getBoardState().nodes.some((n) => n.id === groupNodeId)) {
              throw new Error(CANVAS_NODE_REMOVED);
            }
            writeVideoGroupSlotFromJob(groupNodeId, i, job);
          });
        } catch (err) {
          if (err instanceof Error && err.message === CANVAS_NODE_REMOVED) return;
          patchVideoGroupSlot(groupNodeId, i, {
            status: 'failed',
            errorMessage: err instanceof Error ? err.message : '生成中断，请重试',
          });
        }
      })()
    )
  );

  if (getBoardState().nodes.some((n) => n.id === groupNodeId)) {
    settleVideoGroupSlots(groupNodeId);
  }
}
